import { InteractionRequiredAuthError } from "@azure/msal-browser"
import {
  BrowserTracing,
  Replay,
  init,
  setUser as setSentryUser,
} from "@sentry/react"
import type { AccountInfo } from "@azure/msal-browser"
import config from "@/v2-backoffice-client/config"

// Sample rates are env-driven so operators can dial them up/down per
// environment without a redeploy. Defaults are conservative to keep
// Sentry quota predictable. Override via window.env.TRACES_SAMPLE_RATE
// / REPLAYS_SESSION_SAMPLE_RATE / REPLAYS_ERROR_SAMPLE_RATE.
const env = typeof window === "undefined" ? {} : (window.env ?? {})
const parseRate = (raw: unknown, fallback: number) => {
  const n = typeof raw === "string" ? Number(raw) : Number.NaN
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback
}
const TRACE_SAMPLE_RATE = parseRate(env.TRACES_SAMPLE_RATE, 0.2)
const REPLAY_SESSION_SAMPLE_RATE = parseRate(
  env.REPLAYS_SESSION_SAMPLE_RATE,
  0.1,
)
const REPLAY_ERROR_SAMPLE_RATE = parseRate(env.REPLAYS_ERROR_SAMPLE_RATE, 1)

const BLACKLIST = [
  "js.arcgis.com",
  "arcgis.com/sharing",
  "services.geodataonline.no",
  "segment",
]

function getBaseUrl(url: string) {
  return url.split("?")[0]
}

if (config.isInstrumentationEnabled) {
  init({
    dsn: config.sentryDsn,
    dist: "placepoint-backoffice",
    release: config.version,
    environment: config.envName,
    beforeSend(event, hint) {
      const e = hint.originalException
      if (!e) return event
      if (e instanceof InteractionRequiredAuthError) return null
      if (e instanceof Error) {
        if (/access denied/i.test(e.message)) return null
        if (/could not resolve endpoints/i.test(e.message)) return null
        // (The FUS-900 monitor_window_timeout filter is gone: msal-browser
        // v5 removed hidden-iframe silent renewal, so that failure mode no
        // longer exists — interaction-required surfaces as
        // InteractionRequiredAuthError, filtered above.)
      }
      return event
    },
    beforeBreadcrumb(breadcrumb) {
      const { data } = breadcrumb
      if (data) {
        const { url, from, to } = data
        // filter layers / basemap from geodata
        if (url && BLACKLIST.some((u) => url.includes(u))) {
          return null
        }
        // filter duplicate urls
        if (from && to && getBaseUrl(from) === getBaseUrl(to)) {
          return null
        }
      }
      return breadcrumb
    },
    tracesSampler(ctx) {
      const { request } = ctx
      if (request?.url) {
        const { url } = request
        // used by azure app service for health checks
        if (url.includes("/__hs")) return 0
        if (url.includes("/robots933456.txt")) return 0
        if (url.includes("/robots.txt")) return 0
      }
      return TRACE_SAMPLE_RATE
    },
    tracePropagationTargets: [
      "localhost",
      /placepoint/,
      /v2-backoffice-api/,
      /^\/session/,
      /^\/rest/,
      /^\/api/,
      /^\/graphql/,
    ],
    replaysSessionSampleRate: REPLAY_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: REPLAY_ERROR_SAMPLE_RATE,
    integrations: [
      new BrowserTracing({
        startTransactionOnLocationChange: false,
        shouldCreateSpanForRequest(url) {
          if (BLACKLIST.some((u) => url.includes(u))) {
            return false
          }

          if (url.includes("/__hs")) return false
          if (url.includes("/robots933456.txt")) return false
          if (url.includes("/robots.txt")) return false

          if (url.includes("/graphql")) return true
          if (url.includes("/rest")) return true
          if (url.includes("/api")) return true
          if (url.includes("/session")) return true
          if (url.includes("placepoint.no")) return true

          return false
        },
      }),
      // Session Replay: captures DOM + network for sessions that
      // error out. Default sample is 10% of sessions, 100% of
      // sessions that encounter an error — so any Sentry issue in
      // Backoffice links to a reproducible replay. PII is masked.
      new Replay({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
  })
}

// setUser pins the authenticated user to the active Sentry scope so
// every subsequent event (errors, replays, traces) carries who was
// affected. Backoffice doesn't fetch a CurrentUser GraphQL object —
// the MSAL account is the only identity we have client-side.
export function setUser(account: AccountInfo | null) {
  if (!config.isInstrumentationEnabled) return
  if (!account) {
    setSentryUser(null)
    return
  }
  setSentryUser({
    id: account.localAccountId || account.homeAccountId,
    email: account.username,
    username: account.name,
    tenantId: account.tenantId,
  })
}
